import React from 'react'
import { CircularProgress, Typography, Button } from '@mui/material'
import ArrowBack from '@mui/icons-material/ArrowBack'
import { useNavigate, useParams } from 'react-router'
import { useAvailableIntegrationQuery } from '../../graphql/generated/generated'
import { PageWrapper } from '../page.styles'
import { usePageTitle } from '../../hooks'
import {
	appendJDLinkToHeapLocation,
	trackAnalyticEvent,
	ANALYTICS_LOCATIONS,
	LANDING_ROUTE,
	OAUTH_AUTH_TYPE,
} from '../../utils'
import { CreateForm } from '../../components/create-form/create-form'
import { IframeLink } from '../../components'
import { OAuthIntegrationCreate } from '../../components/oauth-integration/oauth-integration-create/oauth-integration-create'
import { useModal } from '../../providers'
import { getHelpUrl, T3_HELP_URL_KEY } from '../../utils/urls'
import { BreadcrumbsLink, CenteredContainer, HeaderContainer, Section } from './create.styles'

export const T3_SUBSCRIPTION_WARNING = 'This integration is included in T3-as-a-Service subscriptions. There will be an additional monthly charge for other subscription types.'

export const Create = () => {
	usePageTitle( 'Add Integration' )

	const params = useParams<{ id: string }>()
	const [ isFormDirty, setIsFormDirty ] = React.useState( false )

	const { data, isLoading } = useAvailableIntegrationQuery( { id: String( params.id ) } )
	const navigate = useNavigate()
	const { showModal, hideModal } = useModal()
	const { authType, description, name, helpUrls } = data?.integration ?? {}
	const learnMoreUrl = getHelpUrl( T3_HELP_URL_KEY, helpUrls )

	const onBreadcrumbClick = () => {
		if ( isFormDirty ) {
			// Show unsaved changes modal
			showModal( {
				title: 'Unsaved Changes',
				headerText: 'Integration has unsaved changes',
				content: 'Changes to this integration will be lost. Are you sure you want to leave?',
				buttons: [
					{ label: 'No, stay', variant: 'text', color: 'primary', onClick: () => hideModal() },
					{ label: 'Yes, leave',
						variant: 'contained',
						color: 'primary',
						onClick: () => {
							trackAnalyticEvent(
								ANALYTICS_LOCATIONS.CREATE_PAGE,
								'Click',
								appendJDLinkToHeapLocation( 'Bread Crumb Back', name ),
							)
							setIsFormDirty( false )
							navigate( LANDING_ROUTE )
						} },
				],
			} )
		} else {
			trackAnalyticEvent(
				ANALYTICS_LOCATIONS.CREATE_PAGE,
				'Click',
				appendJDLinkToHeapLocation( 'Bread Crumb Back', name ),
			)
			navigate( LANDING_ROUTE )
		}
	}

	if ( isLoading ) {
		return <CircularProgress />
	}
	if ( !data?.integration ) {
		return (
			<CenteredContainer>
				<Typography variant="h3" color="text.secondary">
					Integration not found
				</Typography>
				<Typography variant="body1" color="text.secondary">
					The integration you&apos;re looking for could not be found.
				</Typography>
				<Button
					variant="contained"
					color="primary"
					startIcon={<ArrowBack />}
					onClick={() => navigate( LANDING_ROUTE )}
				>
					Back to Integrations
				</Button>
			</CenteredContainer>
		)
	}

	return (
		<PageWrapper>
			<Section>
				<BreadcrumbsLink
					color="primary.main"
					underline="hover"
					onClick={onBreadcrumbClick}
				>
					<ArrowBack fontSize="inherit" />
					<Button variant="text" aria-label="OEM Integrations">
						<Typography variant="body1" color="primary">
							Integrations
						</Typography>
					</Button>
				</BreadcrumbsLink>
				<HeaderContainer spacing={2} alignItems="left">
					<Typography variant="h2">
						Add {name}
					</Typography>
					<Typography color="text.secondary">
						{description} {learnMoreUrl && <IframeLink href={learnMoreUrl} target="_blank" onClick={() => {
							trackAnalyticEvent(
								ANALYTICS_LOCATIONS.CREATE_PAGE,
								'Click',
								appendJDLinkToHeapLocation( 'Learn More Link', name ),
							)
						}}>
							Learn more.
						</IframeLink>}
					</Typography>
					<Typography color="text.secondary">
						{T3_SUBSCRIPTION_WARNING}
					</Typography>
				</HeaderContainer>
			</Section>
			<Section>
				{authType === OAUTH_AUTH_TYPE ? <OAuthIntegrationCreate integrationKey={params.id ?? ''} /> : <CreateForm id={params.id} isFormDirty={isFormDirty} setIsFormDirty={setIsFormDirty} />}
			</Section>
		</PageWrapper>
	)
}
